import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  SearchableItem,
  fuzzySearch,
  SearchResult,
} from "@/utils/navigation/fuzzySearch";
import { keyboardShortcuts } from "@/utils/navigation/keyboardShortcuts";
import { useAuthStore } from "@/stores/auth/auth-store";

/**
 * Command palette hook for global application navigation and actions
 */
export function useCommandPalette() {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [recentActions, setRecentActions] = useState<string[]>([]);

  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated } = useAuthStore();

  // Base command items
  const baseCommands: SearchableItem[] = useMemo(() => {
    const commands: SearchableItem[] = [
      // Navigation commands
      {
        id: "nav-dashboard",
        title: "Go to Dashboard",
        description: "Navigate to the main dashboard",
        category: "Navigation",
        action: () => navigate("/dashboard"),
        shortcut: ["meta", "shift", "d"],
        icon: "Home",
        keywords: ["home", "main", "overview"],
        priority: 10,
      },
      {
        id: "nav-projects",
        title: "Go to Projects",
        description: "View and manage your projects",
        category: "Navigation",
        action: () => navigate("/projects"),
        shortcut: ["meta", "shift", "p"],
        icon: "FolderOpen",
        keywords: ["project", "list", "manage"],
        priority: 9,
      },
      {
        id: "nav-settings",
        title: "Open Settings",
        description: "Configure application settings",
        category: "Navigation",
        action: () => navigate("/settings"),
        shortcut: ["meta", ","],
        icon: "Settings",
        keywords: ["config", "preferences", "options"],
        priority: 8,
      },

      // Editor commands (if in editor)
      ...(location.pathname.includes("/editor")
        ? [
            {
              id: "editor-save",
              title: "Save Project",
              description: "Save the current project",
              category: "Editor",
              action: () => {
                // This would be connected to the editor save functionality
                const event = new CustomEvent("editor-save");
                window.dispatchEvent(event);
              },
              shortcut: ["meta", "s"],
              icon: "Save",
              keywords: ["save", "store", "persist"],
              priority: 15,
            },
            {
              id: "editor-undo",
              title: "Undo",
              description: "Undo the last action",
              category: "Editor",
              action: () => {
                const event = new CustomEvent("editor-undo");
                window.dispatchEvent(event);
              },
              shortcut: ["meta", "z"],
              icon: "Undo",
              keywords: ["undo", "revert", "back"],
              priority: 12,
            },
            {
              id: "editor-redo",
              title: "Redo",
              description: "Redo the last undone action",
              category: "Editor",
              action: () => {
                const event = new CustomEvent("editor-redo");
                window.dispatchEvent(event);
              },
              shortcut: ["meta", "shift", "z"],
              icon: "Redo",
              keywords: ["redo", "forward", "restore"],
              priority: 11,
            },
            {
              id: "editor-component-palette",
              title: "Toggle Component Palette",
              description: "Show or hide the component palette",
              category: "Editor",
              action: () => {
                const event = new CustomEvent("toggle-component-palette");
                window.dispatchEvent(event);
              },
              shortcut: ["meta", "shift", "c"],
              icon: "Package",
              keywords: ["components", "palette", "sidebar"],
              priority: 8,
            },
            {
              id: "editor-properties",
              title: "Toggle Properties Panel",
              description: "Show or hide the properties panel",
              category: "Editor",
              action: () => {
                const event = new CustomEvent("toggle-properties-panel");
                window.dispatchEvent(event);
              },
              shortcut: ["meta", "shift", "i"],
              icon: "Info",
              keywords: ["properties", "inspector", "details"],
              priority: 8,
            },
          ]
        : []),

      // Quick actions
      {
        id: "action-help",
        title: "Show Keyboard Shortcuts",
        description: "Display all available keyboard shortcuts",
        category: "Help",
        action: () => {
          const event = new CustomEvent("show-shortcuts-help");
          window.dispatchEvent(event);
        },
        shortcut: ["meta", "/"],
        icon: "HelpCircle",
        keywords: ["help", "shortcuts", "keys", "commands"],
        priority: 7,
      },
      {
        id: "action-theme-toggle",
        title: "Toggle Theme",
        description: "Switch between light and dark theme",
        category: "Appearance",
        action: () => {
          const event = new CustomEvent("toggle-theme");
          window.dispatchEvent(event);
        },
        shortcut: ["meta", "shift", "t"],
        icon: "Moon",
        keywords: ["theme", "dark", "light", "mode"],
        priority: 6,
      },
    ];

    // Add logout command if authenticated
    if (isAuthenticated) {
      commands.push({
        id: "auth-logout",
        title: "Sign Out",
        description: "Sign out of your account",
        category: "Account",
        action: () => {
          const event = new CustomEvent("auth-logout");
          window.dispatchEvent(event);
        },
        icon: "LogOut",
        keywords: ["logout", "signout", "exit"],
        priority: 5,
      });
    }

    return commands;
  }, [navigate, location.pathname, isAuthenticated]);

  // Search results
  const searchResults = useMemo(() => {
    return fuzzySearch(baseCommands, query, {
      maxResults: 10,
      minScore: 0.1,
    });
  }, [baseCommands, query]);

  // Handle keyboard navigation
  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (!isOpen) return;

      switch (event.key) {
        case "ArrowDown":
          event.preventDefault();
          setSelectedIndex((prev) =>
            prev < searchResults.length - 1 ? prev + 1 : 0,
          );
          break;

        case "ArrowUp":
          event.preventDefault();
          setSelectedIndex((prev) =>
            prev > 0 ? prev - 1 : searchResults.length - 1,
          );
          break;

        case "Enter":
          event.preventDefault();
          if (searchResults[selectedIndex]) {
            executeCommand(searchResults[selectedIndex]);
          }
          break;

        case "Escape":
          event.preventDefault();
          close();
          break;
      }
    },
    [isOpen, searchResults, selectedIndex],
  );

  // Execute command and update recent actions
  const executeCommand = useCallback((command: SearchResult) => {
    try {
      command.action?.();

      // Update recent actions
      setRecentActions((prev) => {
        const updated = [command.id, ...prev.filter((id) => id !== command.id)];
        return updated.slice(0, 10); // Keep last 10 actions
      });

      close();
    } catch (error) {
      console.error("Error executing command:", error);
    }
  }, []);

  // Open command palette
  const open = useCallback(() => {
    setIsOpen(true);
    setQuery("");
    setSelectedIndex(0);
  }, []);

  // Close command palette
  const close = useCallback(() => {
    setIsOpen(false);
    setQuery("");
    setSelectedIndex(0);
  }, []);

  // Register global keyboard shortcut to open command palette
  useEffect(() => {
    keyboardShortcuts.register({
      id: "command-palette-toggle",
      keys: ["meta", "k"],
      description: "Open command palette",
      category: "Navigation",
      action: open,
    });

    // Alternative shortcut for Windows/Linux
    keyboardShortcuts.register({
      id: "command-palette-toggle-alt",
      keys: ["ctrl", "k"],
      description: "Open command palette",
      category: "Navigation",
      action: open,
    });

    return () => {
      keyboardShortcuts.unregister(["meta", "k"]);
      keyboardShortcuts.unregister(["ctrl", "k"]);
    };
  }, [open]);

  // Keyboard event listener
  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [handleKeyDown]);

  // Reset selection when query changes
  useEffect(() => {
    setSelectedIndex(0);
  }, [query]);

  return {
    isOpen,
    query,
    setQuery,
    searchResults,
    selectedIndex,
    setSelectedIndex,
    recentActions,
    executeCommand,
    open,
    close,
  };
}
